'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { tmdb } from '@/lib/tmdb';

interface ContentItem {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  media_type?: 'movie' | 'tv';
}

export function AnimatedContentBackground() {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch multiple pages to get enough content for all rows
        const [
          trendingMoviesPage1,
          trendingMoviesPage2,
          trendingShowsPage1,
          trendingShowsPage2,
          popularMovies,
          popularShows
        ] = await Promise.all([
          tmdb.getTrendingMovies('week', 1),
          tmdb.getTrendingMovies('week', 2),
          tmdb.getTrendingShows('week', 1),
          tmdb.getTrendingShows('week', 2),
          tmdb.getPopularMovies(1),
          tmdb.getPopularShows(1)
        ]);

        // Combine all content and add media type
        const allContent: ContentItem[] = [
          ...trendingMoviesPage1.results.map((item: any) => ({ ...item, media_type: 'movie' as const })),
          ...trendingMoviesPage2.results.map((item: any) => ({ ...item, media_type: 'movie' as const })),
          ...trendingShowsPage1.results.map((item: any) => ({ ...item, media_type: 'tv' as const })),
          ...trendingShowsPage2.results.map((item: any) => ({ ...item, media_type: 'tv' as const })),
          ...popularMovies.results.map((item: any) => ({ ...item, media_type: 'movie' as const })),
          ...popularShows.results.map((item: any) => ({ ...item, media_type: 'tv' as const }))
        ];

        // Remove duplicates and filter items with posters
        const uniqueContent = allContent
          .filter((item, index, self) =>
            item.poster_path &&
            self.findIndex(i => i.id === item.id && i.media_type === item.media_type) === index
          )
          .slice(0, 120); // Take top 120 items for 6 rows of 20

        // Shuffle the array for random display
        const shuffledContent = uniqueContent.sort(() => Math.random() - 0.5);

        setContent(shuffledContent);
      } catch (error) {
        // Error fetching background content
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Calm, cinematic poster wall: a slow drifting plane of posters, dimmed,
  // softly blurred and vignetted so the auth card stays the focus.
  const ready = !loading && content.length > 0;

  const createRow = (startIndex: number, reverse: boolean = false) => {
    const rowContent = content.slice(startIndex, startIndex + 20);
    if (rowContent.length === 0) return null;

    return (
      <div
        key={startIndex}
        className={`flex w-max gap-3 lg:gap-4 ${reverse ? 'acb-scroll-reverse' : 'acb-scroll'}`}
      >
        {/* Duplicate content for a seamless loop */}
        {[...rowContent, ...rowContent].map((item, index) => (
          <div
            key={`${item.id}-${item.media_type}-${startIndex}-${index}`}
            className="relative h-[168px] w-28 flex-shrink-0 overflow-hidden rounded-lg bg-white/[0.03] lg:h-60 lg:w-40"
          >
            <Image
              src={tmdb.getImageUrl(item.poster_path, 'w300')}
              alt=""
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 112px, 160px"
              priority={startIndex === 0 && index < 6}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-black" aria-hidden>
      {ready && (
        <div className="acb-fade-in absolute -inset-[30%] flex -rotate-12 flex-col justify-center gap-3 opacity-60 blur-[1.5px] saturate-[0.85] lg:gap-4">
          {createRow(0, false)}
          {createRow(20, true)}
          {createRow(40, false)}
          {createRow(60, true)}
          {createRow(80, false)}
          {content.length > 100 && createRow(100, true)}
        </div>
      )}

      {/* Dim + vignette: darkest at the edges and behind the centre column */}
      <div className="absolute inset-0 z-10 bg-black/60" />
      <div
        className="absolute inset-0 z-20"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 45%, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 100%)',
        }}
      />
      <div className="absolute inset-x-0 top-0 z-20 h-32 bg-gradient-to-b from-black/80 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 z-20 h-40 bg-gradient-to-t from-black to-transparent" />

      <style jsx global>{`
        @keyframes acb-scroll {
          from { transform: translate3d(0, 0, 0); }
          to { transform: translate3d(-50%, 0, 0); }
        }
        @keyframes acb-scroll-reverse {
          from { transform: translate3d(-50%, 0, 0); }
          to { transform: translate3d(0, 0, 0); }
        }
        @keyframes acb-fade-in {
          from { opacity: 0; }
          to { opacity: 0.6; }
        }
        .acb-scroll { animation: acb-scroll 180s linear infinite; will-change: transform; }
        .acb-scroll-reverse { animation: acb-scroll-reverse 180s linear infinite; will-change: transform; }
        .acb-fade-in { animation: acb-fade-in 1.2s ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .acb-scroll, .acb-scroll-reverse { animation: none; }
        }
      `}</style>
    </div>
  );
}
